import Phaser from 'phaser';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { BootScene } from '@/game/scenes/BootScene';
import { PreloadScene } from '@/game/scenes/PreloadScene';
import { HomeScene } from '@/game/scenes/HomeScene';
import { SongSelectScene } from '@/game/scenes/SongSelectScene';
import { CatSelectScene } from '@/game/scenes/CatSelectScene';
import { BackgroundSelectScene } from '@/game/scenes/BackgroundSelectScene';
import { TreatSelectScene } from '@/game/scenes/TreatSelectScene';
import { SettingsScene } from '@/game/scenes/SettingsScene';
import { GameplayScene } from '@/game/scenes/GameplayScene';
import { PauseScene } from '@/game/scenes/PauseScene';
import { ResultsScene } from '@/game/scenes/ResultsScene';
import { ChartEditorScene } from '@/game/scenes/ChartEditorScene';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  backgroundColor: '#150e26',
  scale: {
    // NONE hands sizing entirely to applyGameSize() below, rather than Phaser's own
    // FIT/ENVELOP. Plain FIT always preserves the internal 9:16 aspect ratio, which
    // on a container proportionally *wider* than that (see applyGameSize()) means
    // empty margins on the sides -- looking broken for a game whose core mechanic is
    // side-to-side thumb reach across the full screen width.
    mode: Phaser.Scale.NONE,
    autoCenter: Phaser.Scale.NO_CENTER,
    width: INTERNAL_WIDTH,
    height: INTERNAL_HEIGHT,
  },
  input: {
    activePointers: 4,
  },
  scene: [
    BootScene,
    PreloadScene,
    HomeScene,
    SongSelectScene,
    CatSelectScene,
    BackgroundSelectScene,
    TreatSelectScene,
    SettingsScene,
    GameplayScene,
    PauseScene,
    ResultsScene,
    ChartEditorScene,
  ],
};

const game = new Phaser.Game(config);

const GAME_ASPECT = INTERNAL_WIDTH / INTERNAL_HEIGHT;
// Max amount (internal-resolution px) applyGameSize() will crop off the *bottom* of
// the game to fill the screen width -- capped comfortably under the smallest
// bottom-of-screen button margin across every scene (ResultsScene's Retry/Quit sit
// ~133px above the very bottom, the tightest of any scene; nudged up twice now --
// first from ~68px, then again here -- after real iPhone screenshots measured the
// actual crop needed to fully close the gap at ~89px internal px, uncomfortably
// close to the previous 90px cap) so a full-width fill can never clip something the
// player needs to tap.
const MAX_BOTTOM_CROP_PX = 115;

/** Sizes and positions the canvas directly (bypassing Phaser's own FIT/ENVELOP).
 * Always fills the container width first -- for a portrait rhythm game whose core
 * mechanic is side-to-side thumb reach, empty margins on the sides (which plain FIT
 * produces whenever the container is proportionally *wider* than the game's 9:16
 * ratio, e.g. iOS Safari with its toolbar chrome staying fully expanded because
 * gameplay deliberately disables the scroll gesture that would otherwise let it
 * auto-collapse) read as flatly broken in a way empty margins top/bottom don't.
 *
 * Filling width can make the game *taller* than the container when the container is
 * proportionally wider than the game. When that happens, only a small capped amount
 * gets top-anchored and clipped off the bottom (via #game-container's overflow:
 * hidden); if closing the gap would need cropping more than that cap, this falls
 * back to a gentler scale that leaves a small width gap instead, so a full-width fill
 * can never cost more than MAX_BOTTOM_CROP_PX of clipped content. When the container
 * is proportionally taller/narrower than the game (the common case), filling width
 * naturally leaves the leftover height as ordinary letterboxing, centered top/bottom,
 * nothing cropped -- e.g. the same result plain FIT always gave in that regime. */
function applyGameSize(): void {
  const container = document.getElementById('game-container');
  if (!container) return;
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  if (cw <= 0 || ch <= 0) return;

  let displayWidth = cw;
  let displayHeight = cw / GAME_ASPECT;
  let marginTop = 0;

  const overflowPx = displayHeight - ch;
  if (overflowPx > 0) {
    const maxOverflowPx = MAX_BOTTOM_CROP_PX * (cw / INTERNAL_WIDTH);
    if (overflowPx > maxOverflowPx) {
      const scale = (ch + maxOverflowPx) / INTERNAL_HEIGHT;
      displayWidth = INTERNAL_WIDTH * scale;
      displayHeight = INTERNAL_HEIGHT * scale;
    }
    // else: fits within the crop budget as-is, top-anchored (marginTop stays 0) so
    // the (bounded) overflow spills off the bottom, clipped by the container.
  } else {
    // Fits within the container height at full width already -- center the leftover
    // vertical space top/bottom like FIT always did.
    marginTop = -overflowPx / 2;
  }

  const canvas = game.canvas;
  canvas.style.width = `${displayWidth}px`;
  canvas.style.height = `${displayHeight}px`;
  canvas.style.marginTop = `${marginTop}px`;
}

applyGameSize();

// Some mobile browsers (notably iOS Safari, when the address bar/toolbar shows or
// hides) don't reliably fire plain window 'resize' -- visualViewport's resize event
// does, so listen there too as a fail-safe.
window.addEventListener('resize', applyGameSize);
window.addEventListener('orientationchange', applyGameSize);
window.visualViewport?.addEventListener('resize', applyGameSize);

// Belt-and-suspenders gesture prevention on top of the CSS touch-action rules --
// some mobile browsers still fire long-press context menus or rubber-band scroll
// inside a touch-action:none element, which would otherwise interrupt a drag.
const gameContainer = document.getElementById('game-container');
gameContainer?.addEventListener('contextmenu', (e) => e.preventDefault());
gameContainer?.addEventListener(
  'touchmove',
  (e) => {
    e.preventDefault();
  },
  { passive: false }
);

